import { ICellRendererComp, ICellRendererParams } from "ag-grid-community";
import { ModuleManager } from "src/shared/models/module-manager";
import {  MenuItemDef } from "ag-grid-community";
import { AgContextMenuServiceBase } from "src/shared/services/ag-context-menu-service-base";
import { Modules } from "../models/module-info.model";

export default class GroupDrillDownCellRenderer implements ICellRendererComp {
    constructor(
        private contextMenuService?: AgContextMenuServiceBase,
    ) {
        this.contextMenuService = contextMenuService ?? AgContextMenuServiceBase.getServiceBase();
    }

    private eGui: HTMLElement;

    //simplified version of when ag-grid-helper-service checks what options should be in the context menu
    showContextMenuIcon(params, moduleManager) {

        const groupData = params.value;
        const menuItemsByKey: { [key: string]: MenuItemDef } = {};

        if (groupData && groupData?.groupLevel > -1) {

            menuItemsByKey['IntervalReport'] = {
                name: "Open Interval Report",
            };

            if (!moduleManager?.container.dashboardManager.isCXOne) {
                menuItemsByKey['ProfilePreview'] = {
                    name: "Open Profile Preview",
                };
            }

            if (groupData.groupLevel === 1 && groupData.groupType == "E") {
                // Level 1 (Agent) only items

                menuItemsByKey['AgentStateDetails'] = {
                    name: "Agent State Details",
                };

                menuItemsByKey['SuggestCoaching'] = {
                    name: "Suggest Coaching Session",
                };

                menuItemsByKey['GiveAward'] = {
                    name: "Give Award",
                };
            }

            if (!moduleManager?.container?.dashboardManager.isCXOne) {
                menuItemsByKey['ExportSingleRow'] = {
                    name: "Export Row Information",
                };
            }
        }

        const menuItems: (string | MenuItemDef)[] = [];

        // if left empty, it will allow all modules that fit the criteria above
        const ALL_MODULES = new Set([]);

        const giveAwardModules = moduleManager?.container.dashboardManager.isCXOne ? 
        new Set([
            Modules.MetricsSummary,
            Modules.MetricsInterval,
            Modules.CurrentAgentStates,
            Modules.Leaderboard,
        ]) :
        ALL_MODULES;

        const modulesByMenuItem: { [key: string]: Set<Modules> } = {
            IntervalReport: new Set([
                Modules.Summary,
                Modules.Leaderboard,
            ]),
            ProfilePreview: ALL_MODULES,
            AgentStateDetails: ALL_MODULES,
            SuggestCoaching: ALL_MODULES,
            GiveAward: giveAwardModules,
            CreateTask: ALL_MODULES,
            DetailLevelView: ALL_MODULES,
            ExportSingleRow: new Set([
                Modules.Summary,
            ]),
        };

        for (const key of Object.keys(menuItemsByKey ?? {})) {
            const menuItem = menuItemsByKey[key];
            const isCorrectModule = (modulesByMenuItem[key].size === 0 || modulesByMenuItem[key].has(moduleManager?.container.moduleId));
            const hasPermission = this.contextMenuService?.hasPermission[key];

            if (isCorrectModule && hasPermission) {
                if (!moduleManager?.container.dashboardManager.isCXOne) {
                    menuItems.push(menuItem);
                }
                else {
                    if (menuItem.name != 'ProfilePreview')
                        menuItems.push(menuItem);
                }
            }
        }
        return menuItems.length > 0;
    }

    init(params: ICellRendererParams) {
        this.eGui = document.createElement('div');

        // The Breakdown View of the Interval module will show Intervals by Group if chosen in it's settings
        // isIntervalListGroupRow will be used to determine if the group cell renderer shoud be applied to the row group
        if (!params.node.group && params.data == null && !params['isIntervalListGroupRow']) // node.group = ag-grid row group cell, not emp/rec group.
            return;
        this.eGui.classList.add('d-flex', 'h-100', 'align-items-center', 'ag_container_for_hover');

        // Normally the Breakdown View of the Interval module displays the interval in the Group Column.
        // These nodes do not have data objects, but the "aggregate value" (the interval string) is passed as the value.
        // A New setting has been added to the interval breakdown to show interval grouped by Groups
        // If the List by Groups setting is selected for the interval breakdown we should skip this statement 
        if (params.node.group && !params['isIntervalListGroupRow']) {
            this.eGui.innerText = params.value;
            return;
        }

        // For the Current Contact List Module, if group is not assigned say unassigned
        if (typeof params.value === "string" && !params.node.group && params.node.data.Group?.groupID == 0 && !params['isIntervalListGroupRow']) {
            this.eGui.innerHTML = params.value;
            return;
        }

        let isHeader = !!params.data?.isHeader;

        const moduleManager: ModuleManager = params.context?.moduleManager;

        // draw pic if it exists
        if (!isHeader && params.data?.Pic != null && !moduleManager.container.dashboardManager?.isCXOne) {
            const ERROR_URI = `${_BaseURI}/Admin/ImageResizer.aspx?imgid=-2&w=56&h=56`;

            const picContainer: HTMLDivElement = document.createElement('div');
            picContainer.classList.add('rankingPictureContainer');
            const pic: HTMLImageElement = document.createElement('img');
            pic.src = params.data.Pic;
            pic.addEventListener('error', () => {
                if (pic.src !== ERROR_URI)
                    pic.src = ERROR_URI;
            });
            picContainer.appendChild(pic);
            this.eGui.appendChild(picContainer);
        }

        // group name
        // if this is getting the groupData for an Intarval Breakdown Group Row then we will need to get that data from innerRendererParams instead
        const groupData = params['isIntervalListGroupRow'] ? params['groupedRowData'](params.value) : params.data[params.colDef.field];
        const groupSpan: HTMLSpanElement = document.createElement('span');

        //truncate groupName
        groupSpan.style.textOverflow = 'ellipsis';
        groupSpan.style.overflow = 'hidden';
        groupSpan.style.whiteSpace = 'nowrap';

        const groupName = groupData?.groupName ?? '';
        groupSpan.innerText = groupName;

        const writeAccess = moduleManager?.hasWriteAccess() ?? false;
        const readGroupAccess = moduleManager?.hasReadGroupAccess() ?? false;

        if ((writeAccess || readGroupAccess) && !isHeader && groupData && groupData.groupLevel > 1) { // make group clickable for drilldown
            if (!(moduleManager.container.moduleInfo.DTO.SettingRemovePocFilter && groupData.groupLevel < 3 && groupData.groupType === "R")) {
                groupSpan.classList.add('agentName', 'clickable');
                groupSpan.addEventListener('click', (e) => {
                    const groupId = groupData.groupID;
                    moduleManager.drillDown(groupId);
                });
            }
        }

        this.eGui.appendChild(groupSpan);

        if (isHeader || moduleManager?.isModuleSubscriptionPreview) // header doesn't have options menu
            return;

        if (this.showContextMenuIcon(params, moduleManager)) {
            const optionsMenu: HTMLImageElement = document.createElement('img');
            optionsMenu.src = `${_BaseURI}/App_Themes/Standard/Images/CoreTheme/Dashboard/more_dark.svg`;
            optionsMenu.title = 'more options';
            optionsMenu.style.height = '70%';
            optionsMenu.style.width = 'auto';
            optionsMenu.style.cursor = 'pointer';
            optionsMenu.style.right = '5px';
            optionsMenu.classList.add('position-absolute', 'cv-context-menu-icon-hideable', 'l-kebab-menu');
            optionsMenu.addEventListener('click', (e) => {
                params.api['contextMenuFactory'].showMenu(params.node, params.column, groupName, e);
            });
            this.eGui.appendChild(optionsMenu);
        }
    }

    refresh() {
        return false;
    }

    getGui(): HTMLElement {
        return this.eGui;
    }
}

